import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaCrown, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'sonner';
import { trackEvent, ANALYTICS_EVENTS, setUserProperties } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BillingSuccessPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [search] = useSearchParams();
  const sessionId = search.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | success | pending | error
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  // Guard so the polling loop doesn't fire `purchase` twice if it succeeds
  // on a later poll attempt.
  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('Mangler session_id');
      return;
    }
    const token = localStorage.getItem('session_token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };

    let attempts = 0;
    const maxAttempts = 8;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(
          `${API}/billing/checkout-status/${sessionId}`,
          { headers, withCredentials: true }
        );
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setSubscription(data.subscription);
          setStatus('success');
          // Refetch bruger så AuthContext får den nye subscription_type —
          // uden dette bliver topmenu og låse ved med at vise "Gratis".
          try { await checkAuth(); } catch (_) { /* non-blocking */ }
          const plan = data.subscription?.plan || data.plan || null;
          const isLight = typeof plan === 'string' && plan.startsWith('light_');
          const tierName = isLight ? 'Light' : 'Pro';
          toast.success(`Velkommen til ${tierName} 🎉`);
          // purchase — GA4 standard event used in monetisation reports and
          // Funnel Explorations. Fired exactly once per checkout.
          if (!purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            const value = plan === 'yearly' ? 399
                        : plan === 'monthly' ? 49
                        : plan === 'light_monthly' ? 25
                        : null;
            const subType = isLight ? 'light' : 'pro';
            trackEvent(ANALYTICS_EVENTS.PURCHASE, {
              // `transaction_id` is the canonical GA4 dedupe key — re-using
              // Stripe's checkout session id keeps it idempotent.
              transaction_id: sessionId,
              plan,
              currency: 'DKK',
              value,
              subscription_type: plan ? `${subType}_${plan}` : subType,
            });
            // Reflect the new subscription on the GA4 user dimension so
            // post-purchase behaviour can be segmented separately.
            setUserProperties({
              user_role: subType,
              subscription_type: plan ? `${subType}_${plan}` : subType,
            });
          }
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus('pending');
          return;
        }
        setTimeout(poll, 1500);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err.response?.data?.detail || 'Kunne ikke verificere betalingen');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-100 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl text-center" data-testid="billing-success-card">
        {status === 'checking' && (
          <>
            <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Bekræfter betalingen...</h2>
            <p className="text-gray-600 text-sm">Det tager normalt 1–3 sekunder.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <FaCheckCircle className="text-5xl text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Velkommen til Pro!</h2>
            <p className="text-gray-600 mb-4">Du har nu fuld adgang til alle opskrifter.</p>
            {subscription?.pro_current_period_end && (
              <p className="text-sm text-gray-500 mb-6">
                Næste betaling: {new Date(subscription.pro_current_period_end).toLocaleDateString('da-DK')}
              </p>
            )}
            <button
              data-testid="billing-success-continue-btn"
              onClick={() => navigate('/recipes')}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-3 rounded-lg hover:from-yellow-500 hover:to-orange-600"
            >
              Gå til opskrifterne
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <FaCrown className="text-5xl text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Betalingen behandles</h2>
            <p className="text-gray-600 mb-6 text-sm">
              Stripe bekræfter betalingen. Det er normalt klar inden for et minut. Du modtager en kvittering på din e-mail.
            </p>
            <button
              data-testid="billing-pending-continue-btn"
              onClick={() => navigate('/settings')}
              className="w-full bg-gray-100 text-gray-800 font-medium py-3 rounded-lg hover:bg-gray-200"
            >
              Gå til mine indstillinger
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 className="text-xl font-bold mb-2 text-red-600">Noget gik galt</h2>
            <p className="text-gray-600 mb-6 text-sm">{error}</p>
            <button
              data-testid="billing-error-back-btn"
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-800 font-medium py-3 rounded-lg hover:bg-gray-200"
            >
              Tilbage til forsiden
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccessPage;
